import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getAuth } from 'firebase/auth';
import {
  disableNetwork,
  enableNetwork,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';

// Public web config for the NEONFALL Firebase project. These values are not
// secrets — access is gated by Auth + Firestore security rules + App Check.
const firebaseConfig = {
  apiKey: 'AIzaSyB87oKxVIvN6O88s9QG6oRldwZ8opVlCkA',
  // Use our own domain as the auth handler (neon-fall.com is a Hosting site on
  // this project, so it auto-serves /__/auth/*). This keeps the whole OAuth
  // redirect first-party — the default *.firebaseapp.com handler is a third
  // party to the app, and Safari's ITP (and Chrome's third-party-cookie
  // phase-out) block the storage it needs, which silently strands the redirect.
  authDomain: 'neon-fall.com',
  projectId: 'neonfall-726ae',
  storageBucket: 'neonfall-726ae.firebasestorage.app',
  messagingSenderId: '423150334190',
  appId: '1:423150334190:web:e84f9151a9dd436aac43ff',
  measurementId: 'G-3GGPG6106L',
};

export const app = initializeApp(firebaseConfig);

// App Check (reCAPTCHA v3). The site key is PUBLIC — it ships to browsers, like
// the config above. The reCAPTCHA *secret* lives only in the Firebase App Check
// console, never in this repo. Backend enforcement stays gated by the Firestore
// flag config/runtime.enforceAppCheck, so tokens ride along harmlessly until we
// flip it on. In dev, a fixed debug token is used (instead of `true`, which mints
// a new random token every reload) — register this exact string once under
// App Check → Manage debug tokens and it keeps working across restarts.
const RECAPTCHA_SITE_KEY = '6LcMU0ctAAAAAJ8CoKvDQs0j4jgTwf2AZpFYEOh0';
const DEV_APP_CHECK_DEBUG_TOKEN = 'D38120EF-9EE0-4559-9FBB-F9E051567B8C';
if (import.meta.env.DEV) {
  (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
    DEV_APP_CHECK_DEBUG_TOKEN;
}
initializeAppCheck(app, {
  provider: new ReCaptchaV3Provider(RECAPTCHA_SITE_KEY),
  isTokenAutoRefreshEnabled: true,
});

export const auth = getAuth(app);

// Auto-detect long-polling instead of relying on the default WebChannel stream.
// The default transport intermittently stalls behind some proxies/VPNs and on
// Safari, leaving onSnapshot listeners that never fire — the app then hangs on
// "syncing…" after a reload. Auto-detect falls back to long-polling when needed.
//
// persistentLocalCache keeps the last-known Firestore state in IndexedDB, so a
// reload on a flaky connection paints the room/wallet/table instantly from cache
// and reconnects in the background — instead of a cold, network-blocked boot
// (there is no service-worker app-shell cache to fall back on). Multi-tab manager
// so a second tab doesn't fight over the single-tab lease and throw. On browsers
// where IndexedDB is unavailable (Safari private mode, ITP eviction) the SDK
// falls back to an in-memory cache on its own — no cache, but no crash either.
export const db = initializeFirestore(app, {
  experimentalAutoDetectLongPolling: true,
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});

// Mobile browsers suspend the page when it's backgrounded (app switch, screen
// lock), which silently kills Firestore's streaming connection. The SDK can
// take minutes to notice the stream is dead — meanwhile callables still succeed
// (plain HTTPS), so the game advances on the server while this client keeps
// rendering a frozen snapshot and every action bounces with a phase error.
// Bounce the network layer whenever the page comes back so listeners reconnect
// immediately instead of waiting for the SDK's idle timeout.
let bouncingNetwork = false;
export async function bounceFirestoreNetwork() {
  if (bouncingNetwork) return;
  bouncingNetwork = true;
  try {
    await disableNetwork(db);
    await enableNetwork(db);
  } catch {
    // best-effort — the SDK's own reconnect loop is the fallback
  } finally {
    bouncingNetwork = false;
  }
}
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') void bounceFirestoreNetwork();
});
// bfcache restore (e.g. Safari back navigation) skips the normal load path.
window.addEventListener('pageshow', (e) => {
  if (e.persisted) void bounceFirestoreNetwork();
});
window.addEventListener('online', () => void bounceFirestoreNetwork());

// All callables are deployed in europe-west1 — the client MUST match the region.
export const functions = getFunctions(app, 'europe-west1');
